import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"
import { type ApiClientSummary, api, type IssuedKey } from "@/api.js"
import { Async } from "@/components/Async.js"
import { Confirm } from "@/components/Confirm.js"
import { Notice, PageHeader, Section } from "@/components/Page.js"
import { Badge } from "@/components/ui/badge.js"
import { Button } from "@/components/ui/button.js"
import { Card, CardBody } from "@/components/ui/card.js"
import { Input, Label } from "@/components/ui/input.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"

/**
 * A static key per integrated system, so one can be revoked or rotated without
 * touching the others and every request is attributable to a named system in
 * the audit log.
 */
export function ApiClientsScreen() {
  const state = useQuery({ queryKey: ["apiClients"], queryFn: api.apiClients })
  const [creating, setCreating] = useState(false)
  const [issued, setIssued] = useState<IssuedKey | null>(null)

  return (
    <div className="space-y-7">
      <PageHeader
        title="Klucze API"
        actions={
          <Button
            variant={creating ? "subtle" : "primary"}
            onClick={() => {
              setCreating((open) => !open)
              setIssued(null)
            }}
          >
            {creating ? "Anuluj" : "Nowy klucz"}
          </Button>
        }
      />

      {issued !== null && <IssuedKeyPanel issued={issued} onDone={() => setIssued(null)} />}

      {creating && (
        <CreateApiClient
          onIssued={(key) => {
            setIssued(key)
            setCreating(false)
          }}
        />
      )}

      <Async state={state}>
        {(page) => (
          <Table>
            <thead>
              <tr>
                <Th>Identyfikator</Th>
                <Th>Nazwa</Th>
                <Th className="text-right">Decyzje</Th>
                <Th>Ostatnie użycie</Th>
                <Th>Stan</Th>
                <Th className="text-right">Akcje</Th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((apiClient) => (
                <ApiClientRow
                  key={apiClient.apiClientId}
                  apiClient={apiClient}
                  onIssued={setIssued}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Async>
    </div>
  )
}

/** One key's row: identifier, name, usage (count + last used), status, actions. */
function ApiClientRow({
  apiClient,
  onIssued,
}: {
  apiClient: ApiClientSummary
  onIssued: (key: IssuedKey) => void
}) {
  return (
    <Tr>
      <Td className="font-mono text-[13px] font-medium">{apiClient.apiClientId}</Td>
      <Td>{apiClient.name}</Td>
      <Td className="text-right font-mono text-[13px] tabular-nums">{apiClient.decisionCount}</Td>
      <Td
        className="whitespace-nowrap font-mono text-[13px] text-muted-foreground"
        title={apiClient.lastUsedAt ?? undefined}
      >
        {apiClient.lastUsedAt ? new Date(apiClient.lastUsedAt).toLocaleDateString("pl-PL") : "—"}
      </Td>
      <Td>
        <Badge
          tone={apiClient.active ? "allow" : "critical"}
          glyph={apiClient.active ? "✓" : "×"}
          title={apiClient.active ? "active" : "revoked"}
        >
          {apiClient.active ? "aktywny" : "odwołany"}
        </Badge>
      </Td>
      <Td className="text-right">
        <RowActions apiClient={apiClient} onIssued={onIssued} />
      </Td>
    </Tr>
  )
}

/**
 * Per-key actions. Rotating invalidates the old key the instant the new one is
 * shown, and deactivating stops it resolving — both irreversible enough to ask
 * for a confirm in the row rather than firing on one click. A deactivated key is
 * terminal here: it keeps its audit trail but offers no action.
 */
function RowActions({
  apiClient,
  onIssued,
}: {
  apiClient: ApiClientSummary
  onIssued: (key: IssuedKey) => void
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<null | "rotate" | "revoke">(null)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["apiClients"] })

  const rotate = useMutation({
    mutationFn: () => api.rotateApiClientKey(apiClient.apiClientId),
    onSuccess: (key) => {
      void refresh()
      onIssued(key)
      setConfirming(null)
    },
  })
  const revoke = useMutation({
    mutationFn: () => api.revokeApiClient(apiClient.apiClientId),
    onSuccess: () => {
      void refresh()
      setConfirming(null)
    },
  })

  if (!apiClient.active) return <span className="text-muted-foreground">—</span>

  if (confirming !== null) {
    const isRotate = confirming === "rotate"
    const mutation = isRotate ? rotate : revoke
    return (
      <Confirm
        prompt={isRotate ? "Unieważnić stary klucz?" : "Dezaktywować klucz?"}
        pending={mutation.isPending}
        error={mutation.error}
        onConfirm={() => mutation.mutate()}
        onCancel={() => setConfirming(null)}
      />
    )
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Button size="sm" variant="outline" onClick={() => setConfirming("rotate")}>
        Nowy klucz
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirming("revoke")}>
        Dezaktywuj
      </Button>
    </div>
  )
}

/**
 * The issue form. The identifier is a stable slug because it is what the audit
 * log records as the spender — never the key, never a display name that a
 * rename could change out from under history.
 */
function CreateApiClient({ onIssued }: { onIssued: (key: IssuedKey) => void }) {
  const queryClient = useQueryClient()
  const [apiClientId, setApiClientId] = useState("")
  const [name, setName] = useState("")

  const create = useMutation({
    mutationFn: api.createApiClient,
    onSuccess: (key) => {
      void queryClient.invalidateQueries({ queryKey: ["apiClients"] })
      onIssued(key)
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    const id = apiClientId.trim()
    const label = name.trim()
    if (id.length > 0 && label.length > 0) create.mutate({ apiClientId: id, name: label })
  }

  return (
    <Card>
      <CardBody>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block" htmlFor="new-apiClient-id">
                Identyfikator
              </Label>
              <Input
                id="new-apiClient-id"
                className="font-mono text-[13px]"
                value={apiClientId}
                onChange={(event) => setApiClientId(event.target.value)}
                placeholder="whitelabel"
              />
              <p className="mt-1.5 text-[13px] text-muted-foreground">
                Małe litery, cyfry i myślniki. Trafia do logu audytowego jako pytający.
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block" htmlFor="new-apiClient-name">
                Nazwa
              </Label>
              <Input
                id="new-apiClient-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Whitelabel"
              />
            </div>
          </div>

          {create.error !== null && create.error !== undefined && (
            <p className="text-sm text-critical">
              {create.error instanceof Error ? create.error.message : "Nie udało się wydać klucza."}
            </p>
          )}

          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Wydaję…" : "Wydaj klucz"}
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

/**
 * The plaintext key, shown exactly once. It is in this response and nowhere
 * else, ever again — only the hash is stored — so the panel says so plainly and
 * makes copying the one easy thing to do before it is gone.
 */
function IssuedKeyPanel({ issued, onDone }: { issued: IssuedKey; onDone: () => void }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.key)
      setCopied(true)
    } catch {
      // A blocked clipboard (an insecure context, a denied permission) is not
      // an error worth a dialog: the key is on screen to select by hand.
      setCopied(false)
    }
  }

  return (
    <Notice tone="review">
      <Section
        title={`Klucz wydany dla ${issued.apiClientId}`}
        hint="Zapisz go teraz — nie da się go odczytać ponownie."
        actions={
          <Button variant="ghost" size="sm" onClick={onDone}>
            Gotowe
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto rounded-md border bg-card px-3 py-2 font-mono text-[13px] text-foreground">
            {issued.key}
          </code>
          <Button variant="outline" size="sm" onClick={() => void copy()}>
            {copied ? "Skopiowano ✓" : "Kopiuj"}
          </Button>
        </div>
      </Section>
    </Notice>
  )
}
